export type MentionMember = {
  user_id: string;
  email: string;
  display_name: string | null;
  username?: string | null;
};

/** Shared Tailwind selectors for `.mention` chips in editor + saved note HTML. */
export const NOTE_MENTION_CHIP_CLASS =
  '[&_.mention]:text-pipedrive-blue [&_.mention]:font-semibold [&_.mention]:bg-blue-50 [&_.mention]:rounded [&_.mention]:px-0.5 [&_.mention]:cursor-pointer [&_.mention]:hover:bg-blue-100';

export function mentionLabel(member: MentionMember): string {
  return (
    String(member.username ?? '').trim() ||
    member.display_name?.trim() ||
    (member.email.includes('@') ? member.email.split('@')[0] : member.email) ||
    'Nutzer'
  );
}

export function buildMentionSpan(member: MentionMember): string {
  const label = mentionLabel(member);
  return `<span class="mention" contenteditable="false" data-mention-user-id="${member.user_id}">@${label}</span>&nbsp;`;
}

export function createMentionSpanElement(member: MentionMember): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = 'mention';
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-mention-user-id', member.user_id);
  span.textContent = `@${mentionLabel(member)}`;
  return span;
}

export function replaceMentionSpan(span: HTMLElement, member: MentionMember): void {
  span.className = 'mention';
  span.setAttribute('contenteditable', 'false');
  span.setAttribute('data-mention-user-id', member.user_id);
  span.textContent = `@${mentionLabel(member)}`;
}

export function getMentionSpanFromEventTarget(
  target: EventTarget | null,
  editor: HTMLElement,
): HTMLElement | null {
  if (!target || !(target instanceof Node)) return null;
  const el = target instanceof HTMLElement ? target : target.parentElement;
  const span = el?.closest('[data-mention-user-id]') as HTMLElement | null;
  if (!span || !editor.contains(span)) return null;
  return span;
}

export function filterMembersForMentionQuery(
  members: MentionMember[],
  query: string,
  excludeUserId?: string,
): MentionMember[] {
  const q = query.trim().toLowerCase();
  return members
    .filter((m) => m.user_id && m.user_id !== excludeUserId)
    .filter((m) => {
      if (!q) return true;
      const username = mentionLabel(m).toLowerCase();
      const display = (m.display_name ?? '').toLowerCase();
      const email = m.email.toLowerCase();
      return username.includes(q) || display.includes(q) || email.includes(q);
    })
    .slice(0, 8);
}

const MENTION_QUERY_RE = /(?:^|\s)@([a-zA-Z0-9._-]*)$/;

function mentionTokenLengthBeforeCaret(editor: HTMLElement): number | null {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  const preRange = range.cloneRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.startContainer, range.startOffset);
  const match = MENTION_QUERY_RE.exec(preRange.toString());
  if (!match) return null;
  return match[0].length - (match[0].startsWith(' ') ? 1 : 0);
}

function isZeroDomRect(rect: DOMRect): boolean {
  return rect.top === 0 && rect.left === 0 && rect.bottom === 0 && rect.right === 0;
}

/** Anchor for the mention dropdown — based on the @token, not a stray caret after Enter. */
export function getMentionTokenAnchorRect(editor: HTMLElement): DOMRect | null {
  const tokenLen = mentionTokenLengthBeforeCaret(editor);
  if (tokenLen == null) return null;
  const sel = window.getSelection();
  if (!sel?.rangeCount) return null;
  const endRange = sel.getRangeAt(0);
  if (!editor.contains(endRange.startContainer)) return null;

  const anchorRange = endRange.cloneRange();
  let remaining = tokenLen;
  let node: Node | null = endRange.startContainer;
  let offset = endRange.startOffset;

  while (remaining > 0 && node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const textNode = node as Text;
      const take = Math.min(offset, remaining);
      if (take > 0) {
        offset -= take;
        remaining -= take;
        if (remaining === 0) {
          anchorRange.setStart(textNode, offset);
          anchorRange.setEnd(endRange.startContainer, endRange.startOffset);
          const rect = anchorRange.getBoundingClientRect();
          if (!isZeroDomRect(rect)) return rect;
          break;
        }
      }
    }
    const prev = previousEditableTextNode(editor, node, offset);
    if (!prev) break;
    node = prev.node;
    offset = prev.offset;
  }

  const caretRect = endRange.getBoundingClientRect();
  if (!isZeroDomRect(caretRect)) return caretRect;
  const editorRect = editor.getBoundingClientRect();
  return new DOMRect(editorRect.left, editorRect.top + 8, 0, 20);
}

function previousEditableTextNode(
  editor: HTMLElement,
  node: Node,
  offset: number,
): { node: Text; offset: number } | null {
  if (node.nodeType === Node.TEXT_NODE && offset > 0) {
    return { node: node as Text, offset };
  }
  const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
  let last: Text | null = null;
  let current: Node | null;
  while ((current = walker.nextNode())) {
    if (current === node) break;
    last = current as Text;
  }
  if (last) return { node: last, offset: last.length };
  return null;
}

/** Returns @query text before caret in contentEditable, or null if not in a mention token. */
export function getMentionQueryAtCaret(editor: HTMLElement): string | null {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return null;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return null;
  const preRange = range.cloneRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.startContainer, range.startOffset);
  const textBefore = preRange.toString();
  const match = MENTION_QUERY_RE.exec(textBefore);
  if (!match) return null;
  return match[1] ?? '';
}

export function deleteMentionTokenBeforeCaret(editor: HTMLElement): boolean {
  const sel = window.getSelection();
  if (!sel?.rangeCount || !sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return false;
  const preRange = range.cloneRange();
  preRange.selectNodeContents(editor);
  preRange.setEnd(range.startContainer, range.startOffset);
  const textBefore = preRange.toString();
  const match = MENTION_QUERY_RE.exec(textBefore);
  if (!match) return false;
  const deleteLen = match[0].length - (match[0].startsWith(' ') ? 1 : 0);
  for (let i = 0; i < deleteLen; i++) {
    document.execCommand('delete', false);
  }
  return true;
}

export function insertMentionAtCaret(editor: HTMLElement, member: MentionMember): void {
  editor.focus();
  deleteMentionTokenBeforeCaret(editor);
  const sel = window.getSelection();
  if (!sel?.rangeCount) return;
  const range = sel.getRangeAt(0);
  if (!editor.contains(range.startContainer)) return;
  range.collapse(true);
  const span = createMentionSpanElement(member);
  range.insertNode(span);
  const space = document.createTextNode('\u00a0');
  range.setStartAfter(span);
  range.collapse(true);
  range.insertNode(space);
  range.setStartAfter(space);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}
